import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import DashboardLayout from '../components/dashboard/DashboardLayout';
import { RootState } from '../store';
import { useGetUsersId } from '../api/generated/arrayscapeGamingDashboardAPI';
import type { BankTransaction } from '../api/generated/model';
import { useQueryClient } from '@tanstack/react-query';
import TransactionDetailModal from '../components/TransactionDetailModal';
import BankTransactionsTable, { bankTransactionFilterKeys } from '../components/BankTransactionsTable';

const BankTransactionsPage: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const queryClient = useQueryClient();
  const { data: userData } = useGetUsersId(user?.id || '', { query: { enabled: !!user?.id } });
  const [searchParams, setSearchParams] = useSearchParams();

  // Initial state hydrated from URL once.
  const [initialPeriod] = useState<string>(() => searchParams.get('period') || 'month');
  const [initialDateRange] = useState<{ start_date: string; end_date: string }>(() => {
    const urlStart = searchParams.get('start_date');
    const urlEnd = searchParams.get('end_date');
    if (urlStart && urlEnd) return { start_date: urlStart, end_date: urlEnd };
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return {
      start_date: start.toISOString().split('T')[0],
      end_date: end.toISOString().split('T')[0],
    };
  });
  const [initialFilters] = useState<Record<string, any>>(() => {
    const f: Record<string, any> = {};
    for (const key of bankTransactionFilterKeys) {
      const v = searchParams.get(key);
      if (v) f[key] = v;
    }
    return f;
  });
  const [initialSort] = useState<{ field: string; direction: 'asc' | 'desc' }>(() => ({
    field: searchParams.get('sort_field') || 'date',
    direction: (searchParams.get('sort_direction') as 'asc' | 'desc') || 'desc',
  }));

  // Live mirrors of child state, used to drive URL sync.
  const [period, setPeriod] = useState(initialPeriod);
  const [dateRange, setDateRange] = useState(initialDateRange);
  const [filters, setFilters] = useState(initialFilters);
  const [sort, setSort] = useState(initialSort);

  useEffect(() => {
    const params = new URLSearchParams();
    params.set('period', period);
    // 'all' mode is exclusive: never include start/end alongside it.
    if (period !== 'all') {
      if (dateRange.start_date) params.set('start_date', dateRange.start_date);
      if (dateRange.end_date) params.set('end_date', dateRange.end_date);
    }
    for (const key of bankTransactionFilterKeys) {
      if (filters[key]) params.set(key, filters[key]);
    }
    if (sort.field !== 'date') params.set('sort_field', sort.field);
    if (sort.direction !== 'desc') params.set('sort_direction', sort.direction);
    setSearchParams(params, { replace: true });
  }, [period, dateRange, filters, sort, setSearchParams]);

  const [selectedTransaction, setSelectedTransaction] = useState<BankTransaction | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const handleTransactionUpdated = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['bank-transactions:bank_transactions'] });
    setRefreshKey(k => k + 1);
  }, [queryClient]);

  // useCallback so the table's notification effects don't see a fresh
  // reference each render — that would re-fire the effect, push state up,
  // re-render, mint a new ref, ad infinitum.
  const handleDateRangeChange = useCallback(
    (r: { start_date: string; end_date: string }, p: string) => {
      setDateRange(r);
      setPeriod(p);
    },
    []
  );
  const handleFiltersChange = useCallback((f: Record<string, any>) => setFilters(f), []);
  const handleSortChange = useCallback(
    (field: string, direction: 'asc' | 'desc') => setSort({ field, direction }),
    []
  );
  const handleRowClick = useCallback((t: BankTransaction) => setSelectedTransaction(t), []);

  return (
    <DashboardLayout title="Bank Transactions" scrollable>
      <BankTransactionsTable
        initialPeriod={initialPeriod}
        initialDateRange={initialDateRange}
        initialFilters={initialFilters}
        initialSort={initialSort}
        onDateRangeChange={handleDateRangeChange}
        onFiltersChange={handleFiltersChange}
        onSortChange={handleSortChange}
        onRowClick={handleRowClick}
        preferencesKey="bank_transactions"
        userId={user?.id}
        userPreferences={userData?.preferences}
        refreshKey={refreshKey}
      />

      {selectedTransaction && (
        <TransactionDetailModal
          key={selectedTransaction.id}
          transaction={selectedTransaction}
          onClose={() => setSelectedTransaction(null)}
          onUpdated={handleTransactionUpdated}
        />
      )}
    </DashboardLayout>
  );
};

export default BankTransactionsPage;
